import styled from "@emotion/styled"
import { PButton, PHeading, PInputText, PText, PTextarea } from "@porsche-design-system/components-react"
import { breakpoint, spacingStaticLg, spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import { createVehicleShareLinkRoute, generateQrCodeRoute, vehicleShareLinkLimits } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { trackingActionMap, useBffDynamic } from "src/hooks"
import { ShareVehiclesLinkAndQRCode } from "../shareVehiclesModal/components/ShareVehiclesLinkAndQRCode"

const messages = defineMessages({
	back: {
		defaultMessage: "Back",
		id: "inventorylist.shareVehiclesWithMessage.back",
	},
	heading: {
		defaultMessage: "Share vehicles",
		id: "inventorylist.shareVehiclesWithMessage.heading",
	},
	headlineLabel: {
		defaultMessage: "Personalized headline (optional)",
		id: "inventorylist.shareVehiclesWithMessage.headlineLabel",
	},
	intro: {
		defaultMessage: "Create a more personal experience by adding a custom title and a message for sharing link.",
		id: "inventorylist.shareVehiclesWithMessage.intro",
	},
	messageDescription: {
		defaultMessage: "Help your client understand your selection.",
		id: "inventorylist.shareVehiclesWithMessage.messageDescription",
	},
	messageLabel: {
		defaultMessage: "Message for your client (optional)",
		id: "inventorylist.shareVehiclesWithMessage.messageLabel",
	},
	messagePlaceholder: {
		defaultMessage: "Highlight the key reasons for your selection",
		id: "inventorylist.shareVehiclesWithMessage.messagePlaceholder",
	},
	nextStep: {
		defaultMessage: "Next step",
		id: "inventorylist.shareVehiclesWithMessage.nextStep",
	},
	step: {
		defaultMessage: "Step {step} of 2",
		id: "inventorylist.shareVehiclesWithMessage.step",
	},
})

export interface ShareVehiclesWithMessageModalContainerProps {
	/** Listing ids in selection order. */
	listingIds: string[]
}

type Props = ModalProps<ShareVehiclesWithMessageModalContainerProps>

export const ShareVehiclesWithMessageModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { listingIds } = modalProps
	const { formatMessage } = useIntl()
	const { hideModal } = useContext(ModalContext)
	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const [step, setStep] = useState<1 | 2>(1)
	const [title, setTitle] = useState("")
	const [message, setMessage] = useState("")
	const [isCreating, setIsCreating] = useState(false)
	const [shareUrl, setShareUrl] = useState<string>()
	const [qrCodeData, setQrCodeData] = useState<string>()

	const createShareLink = useBffDynamic(createVehicleShareLinkRoute, "inline")
	const generateQrCode = useBffDynamic(generateQrCodeRoute, "inline")

	useEffect(() => {
		if (!shareUrl) {
			return
		}
		generateQrCode({ body: undefined, pathParams: {}, queryParams: { url: shareUrl } })
			.then(({ dataUrl }) => setQrCodeData(dataUrl))
			.catch(() => {
				// shown inline by the QR code section
			})
	}, [generateQrCode, shareUrl])

	const onNextStepClick = useCallback(() => {
		setIsCreating(true)
		createShareLink({ body: { listingIds, message, title }, pathParams: {}, queryParams: {} })
			.then(({ url }) => {
				setQrCodeData(undefined)
				setShareUrl(url)
				setStep(2)
			})
			.catch(() => {
				// shown inline by useBffDynamic
			})
			.finally(() => setIsCreating(false))
	}, [createShareLink, listingIds, message, title])

	const onBackClick = useCallback(() => setStep(1), [])

	const onCopyToClipboard = useCallback(
		() =>
			trackEvent({ action: trackingActionMap.copyShareVehiclesLink, category: "inventory_vehicle_sharing_bar" }),
		[],
	)

	return (
		<Modal id={id} heading={formatMessage(messages.heading)}>
			<Container>
				<PHeading size="xs" tag="h3">
					<FormattedMessage {...messages.step} values={{ step }} />
				</PHeading>

				{step === 1 || !shareUrl ? (
					<>
						<PText size="sm">
							<FormattedMessage {...messages.intro} />
						</PText>
						<PInputText
							name="title"
							data-testid="share-vehicles-title-input"
							label={formatMessage(messages.headlineLabel)}
							value={title}
							maxLength={vehicleShareLinkLimits.maxTitleLength}
							counter
							onInput={(event) => setTitle((event.target as HTMLInputElement).value)}
						/>
						<PTextarea
							name="message"
							data-testid="share-vehicles-message-input"
							label={formatMessage(messages.messageLabel)}
							description={formatMessage(messages.messageDescription)}
							placeholder={formatMessage(messages.messagePlaceholder)}
							value={message}
							maxLength={vehicleShareLinkLimits.maxMessageLength}
							counter
							onInput={(event) => setMessage((event.target as HTMLTextAreaElement).value)}
						/>
						<ErrorNotificationInline />
					</>
				) : (
					<ShareVehiclesLinkAndQRCode
						qrCodeData={qrCodeData}
						url={shareUrl}
						onCopyToClipboard={onCopyToClipboard}
					/>
				)}
			</Container>

			<StyledButtonGroup>
				{step === 1 ? (
					<PButton
						type="button"
						loading={isCreating}
						disabled={listingIds.length === 0}
						data-testid="share-vehicles-next-button"
						onClick={onNextStepClick}
					>
						<FormattedMessage {...messages.nextStep} />
					</PButton>
				) : (
					<PButton type="button" data-testid="share-vehicles-back-button" onClick={onBackClick}>
						<FormattedMessage {...messages.back} />
					</PButton>
				)}
				<PButton type="button" variant="secondary" onClick={hide}>
					<FormattedMessage {...formMessages.close} />
				</PButton>
			</StyledButtonGroup>
		</Modal>
	)
})

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticLg};

	@media (min-width: ${breakpoint.s}px) {
		min-width: 450px;
	}
`

const StyledButtonGroup = styled(ButtonGroup)`
	margin-top: ${spacingStaticXl};
`
